import { useState, useRef } from 'react';
import { Download, Upload, Trash2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLessonStore, type ExportedProgress } from '@/store/lessonStore';
import { useGitStore } from '@/store/gitStore';
import { cn } from '@/lib/utils';

/**
 * Компонент для управления прогрессом курса
 * Позволяет экспортировать, импортировать и сбрасывать прогресс
 */
export function ProgressManager() {
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { exportProgress, importProgress, resetAllProgress, progress } = useLessonStore();
  const { exportState, importState } = useGitStore();

  // Экспорт прогресса в JSON файл
  const handleExport = async () => {
    setIsExporting(true);
    setError(null);
    setSuccess(null);

    try {
      // Получаем состояние Git
      const gitState = exportState();

      // Экспортируем прогресс
      const data = exportProgress(gitState);

      // Создаём Blob и ссылку для скачивания
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);

      // Создаём ссылку для скачивания
      const link = document.createElement('a');
      link.href = url;
      link.download = `git-course-progress-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccess('Прогресс успешно экспортирован');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при экспорте');
    } finally {
      setIsExporting(false);
    }
  };

  // Импорт прогресса из JSON файла
  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setError(null);
    setSuccess(null);

    try {
      const text = await file.text();
      const data = JSON.parse(text) as ExportedProgress;

      // Импортируем прогресс
      const success = importProgress(data);

      if (!success) {
        throw new Error('Некорректный формат файла');
      }

      // Импортируем состояние Git если есть
      if (data.gitState) {
        importState(data.gitState);
      }

      setSuccess('Прогресс успешно импортирован');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при импорте');
    } finally {
      setIsImporting(false);
      // Сбрасываем input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Сброс всего прогресса
  const handleReset = () => {
    if (window.confirm('Вы уверены, что хотите сбросить весь прогресс? Это действие нельзя отменить.')) {
      resetAllProgress();
      setSuccess('Прогресс сброшен');
    }
  };

  // Копирование в буфер обмена
  const handleCopyToClipboard = async () => {
    try {
      const gitState = exportState();
      const data = exportProgress(gitState);
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      setSuccess('Прогресс скопирован в буфер обмена');
    } catch (err) {
      setError('Не удалось скопировать в буфер обмена');
    }
  };

  return (
    <div className="space-y-4">
      {/* Статистика */}
      <div className="rounded-lg bg-slate-800/50 p-3">
        <h4 className="text-xs font-medium text-slate-300 mb-2">Статистика</h4>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="text-slate-500">Завершено уроков:</span>
            <span className="ml-1 text-slate-200">
              {Object.values(progress.lessons).filter((l) => l.completed).length}
            </span>
          </div>
          <div>
            <span className="text-slate-500">Общий прогресс:</span>
            <span className="ml-1 text-slate-200">{progress.overallProgress}%</span>
          </div>
          <div className="col-span-2">
            <span className="text-slate-500">Последняя активность:</span>
            <span className="ml-1 text-slate-200">
              {new Date(progress.lastActivityAt).toLocaleString('ru-RU')}
            </span>
          </div>
        </div>
      </div>

      {/* Уведомления */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-900/30 px-3 py-2 text-xs text-red-400">
          <AlertCircle className="h-3 w-3 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 rounded-lg bg-green-900/30 px-3 py-2 text-xs text-green-400">
          <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Кнопки действий */}
      <div className="space-y-2">
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isExporting}
            className="flex-1"
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            {isExporting ? 'Экспорт...' : 'Экспорт'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isImporting}
            className="flex-1"
          >
            <Upload className="mr-1.5 h-3.5 w-3.5" />
            {isImporting ? 'Импорт...' : 'Импорт'}
          </Button>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleCopyToClipboard}
          className="w-full"
        >
          Копировать в буфер
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={handleReset}
          className="w-full text-red-400 hover:text-red-300 hover:bg-red-900/20"
        >
          <Trash2 className="mr-1.5 h-3.5 w-3.5" />
          Сбросить прогресс
        </Button>
      </div>

      {/* Скрытый input для импорта */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        onChange={handleImport}
        className="hidden"
      />
    </div>
  );
}

/**
 * Компактная версия компонента для встраивания в Header или Sidebar
 */
export function ProgressManagerCompact() {
  const [showMenu, setShowMenu] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { exportProgress, importProgress, progress } = useLessonStore();
  const { exportState, importState } = useGitStore();

  const handleExport = () => {
    const gitState = exportState();
    const data = exportProgress(gitState);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `git-course-progress-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowMenu(false);
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text) as ExportedProgress;
      importProgress(data);
      if (data.gitState) {
        importState(data.gitState);
      }
    } catch (err) {
      console.error('Import failed:', err);
    }

    setShowMenu(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowMenu(!showMenu)}
        className="text-xs"
      >
        <Download className="mr-1 h-3 w-3" />
        Прогресс ({progress.overallProgress}%)
      </Button>

      {showMenu && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowMenu(false)}
          />
          <div className="absolute right-0 top-full z-20 mt-1 w-48 rounded-lg border border-slate-700 bg-slate-800 p-2 shadow-lg">
            <div className="mb-2 px-2 text-xs text-slate-400">
              Завершено: {Object.values(progress.lessons).filter((l) => l.completed).length} уроков
            </div>
            <button
              onClick={handleExport}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-slate-300 hover:bg-slate-700"
            >
              <Download className="h-3 w-3" />
              Экспорт в файл
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-slate-300 hover:bg-slate-700"
            >
              <Upload className="h-3 w-3" />
              Импорт из файла
            </button>
          </div>
        </>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        onChange={handleImport}
        className="hidden"
      />
    </div>
  );
}
